// pages/MarketListingEdits.jsx
// Admin direct-edit of seller listings (hub model — the seller sells to DWI,
// DWI has the final say) and what the seller sees of it.
//   AdminEditListingModal  Admin → DwiSend → Supply: edit any field; "Review
//                          changes" shows exactly what the save will do (server
//                          dry run), "Save" applies it at once.
//   ListingEditHistory     every DWI edit of a listing: who, admin screen or
//                          Claude Code, old → new, shop effects, objections.
//   SellerDwiUpdates       the seller's listing card: "DWI updated …" + Object.
//                          An objection is a flag for DWI — it never undoes an edit.
import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { marketAPI } from '../services/api';
import { Button, Input, Select, Modal, Spinner, Badge } from '../components/ui';

const when = d => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const dateInput = d => (d ? new Date(d).toISOString().slice(0, 10) : '');
const kgInput = g => (g == null ? '' : String(g / 1000));
const textarea = 'mt-1 w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan';

// Only what differs from the listing is sent (the server checks again).
export function changedFields(l, f, productDescription) {
  const out = {};
  const cmp = { productName: l.productName, categoryId: String(l.categoryId), unitId: String(l.unitId), quantity: String(l.quantity), priceUGX: String(l.priceUGX),
    location: l.location, readyDate: dateInput(l.readyDate), notes: l.notes || '', weightKg: kgInput(l.weightGrams) };
  for (const [k, was] of Object.entries(cmp)) {
    if (String(f[k] ?? '') !== String(was ?? '')) out[k] = ['quantity', 'priceUGX'].includes(k) ? Number(f[k]) : k === 'readyDate' ? (f[k] || null) : f[k];
  }
  if (productDescription != null && f.description !== productDescription) out.description = f.description;
  return out;
}

export function AdminEditListingModal({ l, onClose, onSaved }) {
  const [opts, setOpts] = useState(null);
  const [description, setDescription] = useState(null);   // the shop product's, when published
  const [f, setF] = useState({
    productName: l.productName, categoryId: String(l.categoryId), unitId: String(l.unitId), quantity: String(l.quantity), priceUGX: String(l.priceUGX),
    location: l.location, readyDate: dateInput(l.readyDate), notes: l.notes || '', weightKg: kgInput(l.weightGrams), description: '', newStockQty: '',
  });
  const [preview, setPreview] = useState(null);   // { changes, effects, warnings } or { error }
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    Promise.all([marketAPI.listCatalog('category'), marketAPI.listCatalog('unit'), l.productId ? marketAPI.listProducts() : null])
      .then(([c, u, p]) => {
        setOpts({ categories: c.items || [], units: u.items || [] });
        const prod = p?.products?.find(x => String(x._id) === String(l.productId));
        if (prod) { setDescription(prod.description || ''); setF(x => ({ ...x, description: prod.description || '' })); }
      }).catch(err => toast.error(err.message));
  }, [l.productId]);
  const set = k => e => { const v = e.target.value; setF(x => ({ ...x, [k]: v })); setPreview(null); };
  if (!opts) return <Modal open onClose={onClose} title={`Edit ${l.ref} (DWI)`} size="lg"><div className="py-10 flex justify-center"><Spinner /></div></Modal>;
  const unit = opts.units.find(u => String(u._id) === f.unitId);
  const baseChange = l.productId && unit && l.baseUnit && unit.baseUnit !== l.baseUnit;
  const body = () => ({ ...changedFields(l, f, description), ...(baseChange ? { newStockQty: f.newStockQty === '' ? '' : Number(f.newStockQty) } : {}) });
  const review = async () => {
    const b = body();
    if (!Object.keys(b).filter(k => k !== 'newStockQty').length) return setPreview({ error: 'Nothing changed' });
    setBusy(true);
    try { setPreview(await marketAPI.adminEditListing(l._id, { ...b, dryRun: true })); }
    catch (err) { setPreview({ error: err.message }); }
    finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try { await marketAPI.adminEditListing(l._id, body()); toast.success('Saved — applied now, seller told'); onSaved(); }
    catch (err) { toast.error(err.message); setPreview({ error: err.message }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Edit ${l.ref} — ${l.productName} (DWI)`} size="lg">
      <div className="space-y-3">
        <p className="text-xs text-text-muted">You have the final say: changes apply at once{l.productId ? ' — the shop product follows (size, cost → buyer price, stock, description)' : ''}.
          The seller sees each change and can object; an objection never undoes it.</p>
        <Input label="Product" value={f.productName} onChange={set('productName')} />
        <div className="grid sm:grid-cols-2 gap-2">
          <Select label="Category" value={f.categoryId} onChange={set('categoryId')}>{opts.categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}</Select>
          <Select label="Size (sold by)" value={f.unitId} onChange={set('unitId')}>{opts.units.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}</Select>
          <Input label={`Quantity (${unit?.name || ''})`} type="number" min="1" step="1" value={f.quantity} onChange={set('quantity')} />
          <Input label={`Seller price per ${unit?.name || 'unit'} (UGX) — your cost`} type="number" min="1" step="1" value={f.priceUGX} onChange={set('priceUGX')} />
          <Input label={`Weight of one ${unit?.name || 'unit'} (kg, optional)`} inputMode="decimal" value={f.weightKg} onChange={set('weightKg')} />
          <Input label="Ready from (blank = now)" type="date" value={f.readyDate} onChange={set('readyDate')} />
          <Input label="Location" value={f.location} onChange={set('location')} />
          {baseChange && <Input label={`Shop stock in ${unit.baseUnit} (the size is counted in ${unit.baseUnit}, not ${l.baseUnit})`} type="number" min="0" step="1" value={f.newStockQty} onChange={set('newStockQty')} />}
        </div>
        <div>
          <label className="text-xs font-semibold text-text-secondary">Seller's notes</label>
          <textarea rows={2} maxLength={1000} value={f.notes} onChange={set('notes')} className={textarea} />
        </div>
        {description != null && (
          <div>
            <label className="text-xs font-semibold text-text-secondary">Description in the shop (buyers see it; the DRC listing too unless it has French text)</label>
            <textarea rows={3} maxLength={2000} value={f.description} onChange={set('description')} className={textarea} />
          </div>
        )}
        {preview && (
          <div className={`rounded-xl border p-3 text-xs space-y-1 ${preview.error ? 'border-red/50 text-red' : 'border-border'}`}>
            {preview.error ? preview.error : (
              <>
                <div className="font-semibold text-text-primary">This save will:</div>
                {preview.changes.map(c => <div key={c.field}>• {c.label}: {c.from} → <b className="text-text-primary">{c.to}</b></div>)}
                {preview.effects.map(e => <div key={e} className="text-cyan">• {e}</div>)}
                {preview.warnings.map(w => <div key={w} className="text-amber">⚠ {w}</div>)}
              </>
            )}
          </div>
        )}
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="secondary" loading={busy && !preview} onClick={review}>Review changes</Button>
          <Button loading={busy && !!preview} disabled={!preview || !!preview.error} onClick={save}>Save — applies now</Button>
        </div>
      </div>
    </Modal>
  );
}

const VIA = { admin: 'admin screen', 'claude-code': 'Claude Code script' };

export function ListingEditHistory({ listingId, refreshKey = 0 }) {
  const [rows, setRows] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [note, setNote] = useState('');
  const load = useCallback(() => marketAPI.adminListingEdits(listingId).then(r => setRows(r.edits)).catch(err => toast.error(err.message)), [listingId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!rows) return null;
  if (!rows.length) return <div className="text-xs text-text-muted">No DWI edits yet.</div>;
  const groups = [];
  for (const r of rows) { const g = groups.find(x => x.group === r.group); if (g) g.rows.push(r); else groups.push({ group: r.group, rows: [r] }); }
  const review = async () => {
    try { await marketAPI.reviewObjection(reviewing._id, note); toast.success('Marked reviewed — seller told; your edit stays'); setReviewing(null); setNote(''); load(); }
    catch (err) { toast.error(err.message); }
  };
  return (
    <div className="space-y-2">
      {groups.map(({ group, rows: rs }) => (
        <div key={group} className="rounded-xl border border-border p-2 text-xs">
          <div className="text-text-muted mb-1">{when(rs[0].createdAt)} · by {rs[0].byName} ({VIA[rs[0].via]})</div>
          {rs.map(r => (
            <div key={r._id} className="py-0.5">
              <span className="text-text-secondary">{r.label}:</span> {r.from} → <b className="text-text-primary">{r.to}</b>
              {r.objection && (
                <div className={`mt-1 ml-3 ${r.reviewedAt ? 'text-text-muted' : 'text-amber'}`}>
                  ⚠ Seller objected {when(r.objection.at)}: &quot;{r.objection.reason}&quot;
                  {r.reviewedAt ? ` · reviewed ${when(r.reviewedAt)}${r.reviewNote ? ` — "${r.reviewNote}"` : ''}`
                    : <Button size="xs" variant="ghost" onClick={() => setReviewing(r)}>Mark reviewed</Button>}
                </div>
              )}
            </div>
          ))}
          {rs[0].effects?.length > 0 && <div className="mt-1 text-cyan">{rs[0].effects.map(e => <div key={e}>↳ {e}</div>)}</div>}
        </div>
      ))}
      {reviewing && (
        <Modal open onClose={() => setReviewing(null)} title="Mark objection reviewed" size="sm">
          <div className="space-y-3 text-sm">
            <p className="text-xs text-text-muted">This changes no values — your edit stays. To change a value, edit the listing again.</p>
            <Input label="Note to the seller (optional)" value={note} onChange={e => setNote(e.target.value)} maxLength={500} />
            <div className="flex gap-2"><Button variant="secondary" onClick={() => setReviewing(null)}>Cancel</Button><Button onClick={review}>Mark reviewed</Button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// The seller's listing card: DWI's corrections and an Object button on each.
export function SellerDwiUpdates({ updates = [], onChanged }) {
  const [objecting, setObjecting] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (!updates.length) return null;
  const groups = [];
  for (const u of updates) { const g = groups.find(x => x.group === u.group); if (g) g.rows.push(u); else groups.push({ group: u.group, at: u.at, rows: [u] }); }
  const send = async () => {
    setBusy(true);
    try { await marketAPI.objectToEdit(objecting._id, reason); toast.success('Objection sent — DWI will review it. The change stays until DWI decides.'); setObjecting(null); setReason(''); onChanged?.(); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-2">
      {groups.map(g => (
        <div key={g.group} className="rounded-xl border border-cyan/30 bg-cyan/5 p-2 text-xs">
          <div className="font-semibold text-text-primary">DWI updated your listing · {when(g.at)}</div>
          {g.rows.map(u => (
            <div key={u._id} className="flex flex-wrap items-center gap-2 py-0.5">
              <span>• {u.label}: {u.from} → <b className="text-text-primary">{u.to}</b></span>
              {u.objection
                ? <Badge label={u.reviewedAt ? 'Objection reviewed' : 'Objection sent'} color={u.reviewedAt ? 'gray' : 'amber'} />
                : <Button size="xs" variant="ghost" onClick={() => setObjecting(u)}>Object</Button>}
              {u.reviewNote && <span className="text-text-muted">DWI: {u.reviewNote}</span>}
            </div>
          ))}
        </div>
      ))}
      {objecting && (
        <Modal open onClose={() => setObjecting(null)} title="Object to this change" size="sm">
          <div className="space-y-3 text-sm">
            <div>{objecting.label}: {objecting.from} → <b>{objecting.to}</b></div>
            <p className="text-xs text-text-muted">DWI reads your reason and decides. The change stays in place until DWI changes it.</p>
            <div>
              <label className="text-xs font-semibold text-text-secondary">Why do you disagree?</label>
              <textarea rows={3} maxLength={500} value={reason} onChange={e => setReason(e.target.value)} className={textarea} />
            </div>
            <div className="flex gap-2"><Button variant="secondary" onClick={() => setObjecting(null)}>Cancel</Button><Button loading={busy} disabled={!reason.trim()} onClick={send}>Send objection</Button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
